import type {
  QueueResponse,
  RemoveQueueItemResponse,
  ResolveResponse,
  RoomSnapshot,
  SearchResponse,
  SessionResponse,
  SkipResponse,
} from "./types";

interface RuntimeConfig {
  apiBase?: string;
}

declare global {
  interface Window {
    __KEEPTHEREMOTE_CONFIG__?: RuntimeConfig;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterSeconds: number | undefined;

  constructor(
    message: string,
    status: number,
    code = "request_failed",
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code.toLowerCase();
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const viteApiBase: unknown = import.meta.env.VITE_API_BASE;
const configuredBase =
  window.__KEEPTHEREMOTE_CONFIG__?.apiBase ??
  (typeof viteApiBase === "string" && viteApiBase.length > 0
    ? viteApiBase
    : window.location.origin);
const apiBase = configuredBase.replace(/\/+$/, "");

async function request<T>(path: string, init: RequestInit = {}, guestToken?: string): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (guestToken) {
    headers.set("Authorization", `Bearer ${guestToken}`);
  }

  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers,
    credentials: "omit",
  });

  const payload = (await response.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string; retryAfterSeconds?: number };
    message?: string;
  };

  if (!response.ok) {
    const retryHeader = Number(response.headers.get("retry-after"));
    throw new ApiError(
      payload.error?.message ?? payload.message ?? `Request failed (${response.status})`,
      response.status,
      payload.error?.code ?? "request_failed",
      payload.error?.retryAfterSeconds ??
        (Number.isFinite(retryHeader) && retryHeader > 0 ? retryHeader : undefined),
    );
  }

  return payload as T;
}

function roomPath(roomToken: string, suffix = ""): string {
  return `/api/public/rooms/${encodeURIComponent(roomToken)}${suffix}`;
}

export const guestApi = {
  getRoom(roomToken: string, guestToken?: string, signal?: AbortSignal) {
    return request<RoomSnapshot>(roomPath(roomToken), signal ? { signal } : {}, guestToken);
  },

  createSession(roomToken: string, displayName: string) {
    return request<SessionResponse>(roomPath(roomToken, "/sessions"), {
      method: "POST",
      body: JSON.stringify({ displayName }),
    });
  },

  search(roomToken: string, query: string, guestToken: string, signal?: AbortSignal) {
    return request<SearchResponse>(
      roomPath(roomToken, "/search"),
      {
        method: "POST",
        body: JSON.stringify({ query }),
        ...(signal ? { signal } : {}),
      },
      guestToken,
    );
  },

  resolve(roomToken: string, url: string, guestToken: string, signal?: AbortSignal) {
    return request<ResolveResponse>(
      roomPath(roomToken, "/resolve"),
      {
        method: "POST",
        body: JSON.stringify({ url }),
        ...(signal ? { signal } : {}),
      },
      guestToken,
    );
  },

  addToQueue(roomToken: string, videoId: string, guestToken: string) {
    return request<QueueResponse>(
      roomPath(roomToken, "/queue"),
      {
        method: "POST",
        body: JSON.stringify({ videoId }),
      },
      guestToken,
    );
  },

  removeFromQueue(roomToken: string, itemId: string, guestToken: string) {
    return request<RemoveQueueItemResponse>(
      roomPath(roomToken, `/queue/${encodeURIComponent(itemId)}`),
      { method: "DELETE" },
      guestToken,
    );
  },

  skipCurrent(roomToken: string, guestToken: string) {
    return request<SkipResponse>(
      roomPath(roomToken, "/player/skip"),
      { method: "POST" },
      guestToken,
    );
  },
};

export function roomTokenFromLocation(pathname = window.location.pathname): string | null {
  const match = pathname.match(/^\/r\/([^/?#]+)/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function isYouTubeInput(value: string): boolean {
  try {
    const trimmed = value.trim();
    const parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    return [
      "youtube.com",
      "m.youtube.com",
      "music.youtube.com",
      "youtube-nocookie.com",
      "youtu.be",
    ].includes(host);
  } catch {
    return false;
  }
}
